/**
 * Where the mid-session difficulty range is drawn INLINE, and where it moves behind the masthead
 * trigger instead. Two class strings, exact complements of each other, so the control is on screen
 * exactly once at every viewport.
 *
 * These are not a fresh judgement. The pre-023 page carried FOUR separately-gated copies of the
 * slider and the union of their gates is the answer to "does this viewport have room for it" — a
 * question that had already been measured per band. Rather than keep four copies, the SAME control
 * takes their combined gate:
 *
 * | Viewport                                        | Inline? | Why (pre-023 gate)                  |
 * |-------------------------------------------------|---------|-------------------------------------|
 * | >= 768w, tall (1920x1080, 1280x720, 768x1024)   | yes     | the desktop copy                    |
 * | 1093x614, 1097x617, 1366x633 (laptop / 125-175%)| yes     | short-ls stops at 600h — measured   |
 * |                                                 |         | to leave the hand 250+px, so it fits|
 * | >= 1024w <= 600h, and 951-1023w x 501-600h      | NO      | short-ls: the control clipped the   |
 * |   (1920x1080 at ~200%)                          |         | hand display                        |
 * | phone portrait >= 821h (430x932, 393x873)       | yes     | tall-phone-pt, minus snug-phone-pt  |
 * | phone portrait 731-820h (375x812 iPhone X/XS)   | NO      | snug-phone-pt hid it outright       |
 * | phone portrait <= 730h (375x667, 320x568)       | NO      | below tall-phone-pt                 |
 * | phone landscape 700-950w, >= 400h (900x420,     | yes     | wide-phone-ls                       |
 * |   832x575, 832x623 unfolded folds)              |         |                                     |
 * | phone landscape 951-1023w x 400-500h (956x440)  | yes     | wide-phone-ls (031)                 |
 * | phone landscape < 400h (844x390, 754x303)       | NO      | below wide-phone-ls' floor          |
 * | < 700w landscape (a resized window)             | NO      | no copy ever covered it             |
 *
 * So: hidden for medium-and-shorter phones in BOTH orientations, and for the short-but-wide
 * viewports a big screen reaches at 200% zoom. Everywhere else it stays inline, where it belongs —
 * in the frame's controls row, spanning the panels it filters.
 *
 * ORDER IS LOAD-BEARING. Tailwind emits variants in `screens` order (tailwind.config.js), so among
 * the queries matching a given viewport the LAST one wins, and each later clause carves an exception
 * out of an earlier one. The sequence is
 * md -> tall-phone-pt -> snug-phone-pt -> wide-phone-ls -> short-ls -> squat-ls: `md` turns the row
 * on for everything desktop-width, `tall-phone-pt` turns it on for tall portrait phones,
 * `snug-phone-pt` takes it back off the 731-820h band, `wide-phone-ls` turns it on for landscape
 * phones with height to spare, and the last two take it off the short-and-wide viewports and the
 * squat landscape ones. Reordering these strings changes behaviour even though the classes are the
 * same; adding a screen between them in tailwind.config.js does too.
 *
 * NO `narrow:` CLAUSE, deliberately, though it looks like the natural way to say "not on a phone".
 * index.css force-applies `narrow:` display utilities at specificity 0-2-0 in the 700-950 x 300-550
 * band (the doubled-selector block, there to beat `md:` regardless of source order). At 0-2-0 it
 * would also beat `wide-phone-ls:block` at 0-1-0, and take the control off exactly the 400-550h
 * landscape phones that are supposed to keep it. `squat-ls` states the same exclusion positively and
 * competes on equal footing. The pre-023 page avoided the collision by accident — its landscape copy
 * carried no `narrow:` class at all.
 */

/** The frame's `controls` row (TableFrame's `controlsClassName`). Hides the row, padding and all. */
export const DIFFICULTY_RANGE_INLINE =
  'hidden md:block tall-phone-pt:block snug-phone-pt:hidden wide-phone-ls:block short-ls:hidden squat-ls:hidden';

/**
 * The masthead trigger and the popup overlay it opens — the complement of the row above, clause for
 * clause with `block`/`hidden` swapped for `hidden`/`flex` (both are flex containers).
 */
export const DIFFICULTY_RANGE_COMPACT =
  'md:hidden tall-phone-pt:hidden snug-phone-pt:flex wide-phone-ls:hidden short-ls:flex squat-ls:flex';
